import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faFilter,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { BLOCK_FILTER_CHIPS, CUSTOMER_TYPES } from './blocksExplorerHelpers'

export const BLOCK_TABLE_FILTERS = [
  { key: 'all', label: 'All block types' },
  { key: 'numeric', label: 'Numeric' },
  { key: 'categorical', label: 'Categorical' },
]

const SEARCH_DEBOUNCE_MS = 300

// Every URL change makes SecureRoute fetch a new token and re-render the app, so typing is held
// here and only written to the URL once it pauses.
function useDebouncedQuery(query, onQueryChange) {
  const [draft, setDraft] = useState(query)
  // The latest handler, so a filter changed while the timer ran is not written back over.
  const latestOnQueryChange = useRef(onQueryChange)
  latestOnQueryChange.current = onQueryChange

  // Back/forward or another control can change the query under the box.
  useEffect(() => {
    setDraft(query)
  }, [query])

  useEffect(() => {
    if (draft === query) return undefined
    const timer = setTimeout(
      () => latestOnQueryChange.current(draft),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [draft])

  return [draft, setDraft]
}

function MoreFilters({ blockTable, onBlockTableChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const activeCount = blockTable === 'all' ? 0 : 1

  useEffect(() => {
    if (!isOpen) return undefined
    const closeOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false)
    }
    document.addEventListener('click', closeOutside)
    return () => document.removeEventListener('click', closeOutside)
  }, [isOpen])

  const choose = (key) => {
    onBlockTableChange(key)
    setIsOpen(false)
  }

  return (
    <div
      ref={containerRef}
      className={`dropdown is-right lox-blocks-explorer__more ${
        isOpen ? 'is-active' : ''
      }`}
    >
      <div className="dropdown-trigger">
        <button
          type="button"
          className={`lox-blocks-explorer__more-button ${
            activeCount > 0 ? 'is-active' : ''
          }`}
          aria-haspopup="true"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <FontAwesomeIcon icon={faFilter} />
          More filters
          {activeCount > 0 && (
            <span className="lox-blocks-explorer__chip-count">
              {activeCount}
            </span>
          )}
          <FontAwesomeIcon icon={faChevronDown} className="is-size-7" />
        </button>
      </div>
      {isOpen && (
        <div className="dropdown-menu" role="menu">
          <div className="dropdown-content">
            <p className="dropdown-item lox-blocks-explorer__more-heading">
              Block type
            </p>
            {BLOCK_TABLE_FILTERS.map((option) => (
              <button
                type="button"
                key={option.key}
                role="menuitemradio"
                aria-checked={option.key === blockTable}
                className={`dropdown-item lox-blocks-explorer__menu-item ${
                  option.key === blockTable ? 'is-active' : ''
                }`}
                onClick={() => choose(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function BlocksToolbar({
  customerType,
  onCustomerTypeChange,
  query,
  onQueryChange,
  chip,
  onChipChange,
  counts,
  blockTable,
  onBlockTableChange,
}) {
  const [draftQuery, setDraftQuery] = useDebouncedQuery(query, onQueryChange)
  return (
    <div className="lox-blocks-explorer__toolbar" data-testid="blocks-toolbar">
      <div className="lox-blocks-explorer__toolbar-row">
        <p className="control has-icons-left lox-blocks-explorer__search">
          <input
            className="input is-small"
            type="search"
            placeholder="Search by id, name, attribute, condition"
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            data-testid="blocks-search"
          />
          <span className="icon is-small is-left">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </span>
        </p>
        <div
          className="lox-blocks-explorer__segmented"
          role="group"
          aria-label="Lead customer type"
        >
          {CUSTOMER_TYPES.map((type) => (
            <button
              type="button"
              key={type}
              className={type === customerType ? 'is-selected' : ''}
              aria-pressed={type === customerType}
              onClick={() => onCustomerTypeChange(type)}
              data-testid={`customer-type-${type}`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>
      <div className="lox-blocks-explorer__toolbar-row">
        <div className="lox-blocks-explorer__chips">
          {BLOCK_FILTER_CHIPS.map((filterChip) => (
            <button
              type="button"
              key={filterChip.key}
              className={`lox-blocks-explorer__chip ${
                filterChip.key === chip ? 'is-active' : ''
              }`}
              aria-pressed={filterChip.key === chip}
              onClick={() => onChipChange(filterChip.key)}
              data-testid={`blocks-filter-chip-${filterChip.key}`}
            >
              {filterChip.label}
              <span className="lox-blocks-explorer__chip-count">
                {counts?.[filterChip.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <MoreFilters
          blockTable={blockTable}
          onBlockTableChange={onBlockTableChange}
        />
      </div>
      {customerType === 'KNOWN' && (
        <p className="is-size-7 has-text-grey">
          KNOWN leads are evaluated against NEW blocks.
        </p>
      )}
    </div>
  )
}
